"use client";

import * as React from "react";
import {
    type ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
} from "@tanstack/react-table";
import type {IGetSalonEmployeesByOwnerIdAction} from "@/actions/owner/types";
import {OwnerCompanyEmployeesTableColumns} from "./columns";
import {Input} from "@/components/ui/input";
import {Button} from "@/components/ui/button";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "@/components/ui/select";
import {Search} from "lucide-react";
import {useToast} from "@/hooks/use-toast";
import hooks from "@/hooks";
import Modal from "@/components/core/Modal";
import MultipleSelector from "../../Forms/Fields/MultipleSelector";

interface IOwnerCompanyWorkingHoursTable {
    data: IGetSalonEmployeesByOwnerIdAction["Response"];
    availableVocations?: {
        id: string;
        name: string;
        value: string;
        duration: string;
        salonId: string;
        created_at: string;
        updated_at?: string;
        deleted_at?: string;
    }[];
}

export default function OwnerSalonEmployeesTable({
                                                     data,
                                                     availableVocations = [],
                                                 }: IOwnerCompanyWorkingHoursTable) {
    const [employeesData, setEmployeesData] = React.useState(data);

    React.useEffect(() => {
        setEmployeesData(data);
    }, [data]);

    const {toast} = useToast();

    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        [],
    );
    const [globalFilter, setGlobalFilter] = React.useState("");

    const [selectedVocation, setSelectedVocation] = React.useState("");
    const [editModalOpen, setEditModalOpen] = React.useState(false);
    const [selectedEmployee, setSelectedEmployee] = React.useState<
        IGetSalonEmployeesByOwnerIdAction["Response"][number] | null
    >(null);

    const deleteEmployee = hooks.useDeleteEmployee({});

    const activeEmployee = hooks.useActiveEmployee({});

    const handleToggleActive = async (employeeId: string, isActive: boolean) => {
        try {
            if (!isActive) {
                await deleteEmployee.mutateAsync({
                    userId: employeeId,
                });
            } else {
                await activeEmployee.mutateAsync({
                    userId: employeeId,
                });
            }
            setEmployeesData((prev) =>
                prev.map((emp) =>
                    emp.id === employeeId ? {...emp, active: isActive} : emp,
                ),
            );
            toast({
                title: isActive ? "Colaborador ativado" : "Colaborador desativado",
                description: "Status atualizado com sucesso.",
                variant: "success",
            });
        } catch (error) {
            toast({
                title: "Falha ao atualizar o status",
                description: "Não foi possível atualizar o status do colaborador.",
                variant: "warning",
            });
        }
    };

    const handleOpenEditModal = (
        employee: IGetSalonEmployeesByOwnerIdAction["Response"][number],
    ) => {
        setSelectedEmployee(employee);
        setEditModalOpen(true);
    };

    const updateEmployee = hooks.useUpdateEmployee({});

    const handleSaveVocations = async (
        employeeId: string,
        vocationIds: string[],
    ) => {
        try {
            await updateEmployee.mutateAsync({
                user_id: employeeId,
                vocation_ids: vocationIds,
            });

            toast({
                title: "Profissões atualizadas",
                description:
                    "As profissões do funcionário foram atualizadas com sucesso.",
                variant: "success",
            });
        } catch (error) {
            toast({
                title: "Falha ao atualizar as profissões",
                description: "Não foi possível atualizar as profissões do funcionário.",
                variant: "warning",
            });
            throw error;
        }
    };

    const uniqueVocations = React.useMemo(() => {
        const vocationsSet = new Set<string>();

        if (employeesData.length > 0) {
            employeesData.forEach((employee) => {
                if (employee.vocations && employee.vocations.length > 0) {
                    employee.vocations.forEach((vocation) => {
                        if (vocation.name) {
                            vocationsSet.add(vocation.name);
                        }
                    });
                }
            });
        }

        return Array.from(vocationsSet).sort();
    }, [employeesData]);

    const formatWhatsAppNumber = (number: string) => {
        return number.replace(/^55(\d{2})(\d{5})(\d{4})$/, "+55 ($1) $2-$3");
    };

    const table = useReactTable({
        data: employeesData,
        columns: OwnerCompanyEmployeesTableColumns(
            handleToggleActive,
            handleOpenEditModal,
        ),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        initialState: {
            pagination: {
                pageSize: 5,
            },
        },
        globalFilterFn: (row, columnId, filterValue) => {
            const searchValue = (filterValue ?? "").toLowerCase();
            const cellValue = String(row.getValue(columnId) ?? "").toLowerCase();

            return Boolean(
                cellValue.includes(searchValue) ||
                (row.original.vocations &&
                    row.original.vocations.some((vocation) =>
                        vocation.name.toLowerCase().includes(searchValue),
                    )),
            );
        },
        filterFns: {
            phoneFilter: (row, columnId, filterValue) => {
                const searchValue = (filterValue ?? "").toLowerCase();
                const phone = String(row.getValue(columnId) ?? "").toLowerCase();
                // Remove formatting to search raw phone numbers
                const rawPhone = phone.replace(/\D/g, "");
                const rawSearchValue = searchValue.replace(/\D/g, "");

                return phone.includes(searchValue) || rawPhone.includes(rawSearchValue);
            },
        },
    });

    React.useEffect(() => {
        if (selectedVocation === "all" || selectedVocation === "") {
            table.getColumn("vocation")?.setFilterValue("");
        } else {
            table.getColumn("vocation")?.setFilterValue(selectedVocation);
        }
    }, [selectedVocation, table]);

    return (
        <>
            <div className="w-full p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0 mb-4">
                    <div className="flex flex-wrap gap-3">
                        <div className="relative w-full xs:w-64 sm:w-56 md:w-64 lg:w-72 xl:w-56">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground"/>
                            <Input
                                placeholder="Buscar..."
                                value={globalFilter ?? ""}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                className="pl-8"
                                style={{borderRadius: "999px"}}
                            />
                        </div>

                        <div className="relative w-full xs:w-64 sm:w-56 md:w-64 lg:w-72 xl:w-56">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground"/>
                            <Input
                                placeholder="Buscar por telefone..."
                                value={
                                    (table.getColumn("phone")?.getFilterValue() as string) ?? ""
                                }
                                onChange={(e) =>
                                    table.getColumn("phone")?.setFilterValue(e.target.value)
                                }
                                className="pl-8"
                                style={{borderRadius: "999px"}}
                            />
                        </div>

                        <div className="relative w-full xs:w-64 sm:w-56 md:w-64 lg:w-72 xl:w-56">
                            <Select
                                value={selectedVocation}
                                onValueChange={setSelectedVocation}
                            >
                                <SelectTrigger
                                    className="w-full"
                                    style={{borderRadius: "999px"}}
                                >
                                    <SelectValue placeholder="Filtrar por serviço"/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas os serviços</SelectItem>
                                    {uniqueVocations.map((vocation) => (
                                        <SelectItem key={vocation} value={vocation}>
                                            {vocation}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Itens por página:
            </span>
                        <Select
                            value={table.getState().pagination.pageSize.toString()}
                            onValueChange={(value) => {
                                table.setPageSize(Number(value));
                            }}
                        >
                            <SelectTrigger className="h-8 w-16">
                                <SelectValue
                                    placeholder={table.getState().pagination.pageSize}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                                    <SelectItem key={pageSize} value={pageSize.toString()}>
                                        {pageSize}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="space-y-1 overflow-x-auto">
                    <table className="w-full md:min-w-[600px]">
                        <thead>
                        <tr>
                            {table.getFlatHeaders().map((header) => (
                                <th
                                    key={header.id}
                                    className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer select-none"
                                    onClick={header.column.getToggleSortingHandler()}
                                >
                                    {header.isPlaceholder ? null : (
                                        <div className="flex items-center gap-1">
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                            {header.column.getCanSort() && (
                                                <div className="flex flex-col ml-1">
                                                    {header.column.getIsSorted() === "asc" ? (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="14"
                                                            height="14"
                                                            style={{width: "12px", height: "13px"}}
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="text-primary"
                                                        >
                                                            <path d="m5 15 7-7 7 7"/>
                                                        </svg>
                                                    ) : header.column.getIsSorted() === "desc" ? (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="14"
                                                            height="14"
                                                            style={{width: "12px", height: "13px"}}
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="text-primary"
                                                        >
                                                            <path d="m19 9-7 7-7-7"/>
                                                        </svg>
                                                    ) : (
                                                        <div className="flex flex-col opacity-40">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="14"
                                                                height="14"
                                                                style={{width: "12px", height: "13px"}}
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                className="-mb-1"
                                                            >
                                                                <path d="m5 15 7-7 7 7"/>
                                                            </svg>
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="14"
                                                                height="14"
                                                                style={{width: "12px", height: "13px"}}
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                className="-mt-1"
                                                            >
                                                                <path d="m19 9-7 7-7-7"/>
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                className={`${row.index % 2 === 1 ? "bg-muted/50" : "bg-white"}`}
                            >
                                {row.getVisibleCells().map((cell) => {
                                    return (
                                        <td key={cell.id} className="py-3 px-4">
                                            {cell.column.id === "phone"
                                                ? formatWhatsAppNumber(String(cell.getValue()))
                                                : flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="text-sm text-muted-foreground">
                        Mostrando{" "}
                        <strong>
                            {table.getFilteredRowModel().rows.length == 0
                                ? 0
                                : table.getState().pagination.pageIndex *
                                table.getState().pagination.pageSize +
                                1}
                        </strong>{" "}
                        a{" "}
                        <strong>
                            {Math.min(
                                (table.getState().pagination.pageIndex + 1) *
                                table.getState().pagination.pageSize,
                                table.getFilteredRowModel().rows.length,
                            )}
                        </strong>{" "}
                        de <strong>{table.getFilteredRowModel().rows.length}</strong>{" "}
                        resultados
                    </div>
                    <div className="flex items-center justify-center sm:justify-end space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            Anterior
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({length: table.getPageCount()}, (_, i) => (
                                <Button
                                    key={i}
                                    variant={
                                        i === table.getState().pagination.pageIndex
                                            ? "default"
                                            : "outline"
                                    }
                                    size="sm"
                                    className="w-8 h-8"
                                    onClick={() => table.setPageIndex(i)}
                                >
                                    {i + 1}
                                </Button>
                            )).slice(
                                Math.max(0, table.getState().pagination.pageIndex - 1),
                                Math.min(
                                    table.getPageCount(),
                                    table.getState().pagination.pageIndex + 4,
                                ),
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            Próximo
                        </Button>
                    </div>
                </div>
            </div>

            {selectedEmployee && (
                <Modal
                    isOpen={editModalOpen}
                    onOpenChange={setEditModalOpen}
                    dialogTitle="Editar profissões"
                    dialogDescription={`Escolha as profissões para ${selectedEmployee.name}`}
                    className="flex flex-col gap-6 p-6 sm:p-8 max-w-md overflow-visible"
                >
                    <div className="w-full flex-1">
                        <div>
                            <div className="mb-3">
                <span className="font-semibold text-base">
                  Editar Colaborador
                </span>
                            </div>
                            <div className="grid">
                                <div>
                                    <span className="font-semibold text-sm">Nome: </span>{" "}
                                    <span className="text-sm">{selectedEmployee.name}</span>
                                </div>
                                <div>
                                    <span className="font-semibold text-sm">Telefone: </span>{" "}
                                    <span className="text-sm">
                    {formatWhatsAppNumber(selectedEmployee.phone)}
                  </span>
                                </div>
                            </div>
                        </div>

                        <label
                            htmlFor="vocation"
                            className="text-sm font-semibold mb-2 mt-3 block"
                        >
                            Serviços
                        </label>
                        <MultipleSelector
                            value={selectedEmployee.vocations.map((v) => ({
                                label: v.name,
                                value: v.id,
                            }))}
                            onChange={(newValue) => {
                                setSelectedEmployee((prev) =>
                                    prev
                                        ? {
                                            ...prev,
                                            vocations: availableVocations.filter((v) =>
                                                newValue.some((item) => item.value === v.id),
                                            ),
                                        }
                                        : null,
                                );
                            }}
                            defaultOptions={availableVocations.map((v) => ({
                                label: v.name,
                                value: v.id,
                            }))}
                            openUpwards={false}
                            placeholder="Selecione..."
                            emptyIndicator="Nenhuma vocação encontrada."
                            badgeClassName="bg-[#4b5563] text-content-primary  hover:bg-[#35393e]  durantion-300 ease-linear"
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setEditModalOpen(false)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            onClick={async () => {
                                if (selectedEmployee) {
                                    const selectedIds = selectedEmployee.vocations.map(
                                        (v) => v.id,
                                    );

                                    if (selectedIds.length == 0) {
                                        toast({
                                            title: "Profissão não selecionada",
                                            description: "Selecione uma profissão para continuar.",
                                            variant: "warning",
                                        });

                                        return;
                                    }

                                    await handleSaveVocations(selectedEmployee.id, selectedIds);

                                    setEmployeesData((prev) =>
                                        prev.map((emp) =>
                                            emp.id === selectedEmployee.id
                                                ? {...emp, vocations: selectedEmployee.vocations}
                                                : emp,
                                        ),
                                    );

                                    setEditModalOpen(false);
                                }
                            }}
                        >
                            Salvar
                        </Button>
                    </div>
                </Modal>
            )}
        </>
    );
}
